import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { SectionClouds } from "./SectionClouds";

interface QA {
  q: string;
  a: string;
}

export function FAQ() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = React.useState(false);
  const [open, setOpen] = React.useState<number>(0);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const faqs = t("landing.faq.items", { returnObjects: true }) as QA[];

  return (
    <section ref={ref} id="faq" className="frame bg-surface-2/40">
      <SectionClouds variant="faq" />
      <div className="container-narrow section-pad relative z-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
          <div
            className={cn(
              "transition-all duration-700",
              isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
            )}
          >
            <span className="eyebrow">
              <span className="num">08 —</span> {t("landing.faq.eyebrow")}
            </span>
            <h2 className="section-title" style={{ fontSize: "clamp(28px, 4vw, 44px)" }}>
              <Trans
                i18nKey="landing.faq.title"
                components={{ italic: <span className="serif-italic" /> }}
              />
            </h2>
            <p className="section-lede">{t("landing.faq.lede")}</p>
            <a
              href="mailto:hello@holymoly.dev"
              className="mt-6 inline-flex items-center gap-1 text-[14px] font-medium text-ink transition-colors hover:text-accent"
            >
              {t("landing.faq.askLink")}
            </a>
          </div>

          <div className="space-y-2.5">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              const panelId = `faq-panel-${i}`;
              const headerId = `faq-header-${i}`;
              return (
                <div
                  key={i}
                  className={cn(
                    "overflow-hidden rounded-[16px] border border-line bg-surface",
                    "transition-all duration-700",
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
                  )}
                  style={{
                    transitionDelay: isVisible ? `${i * 60}ms` : "0ms",
                  }}
                >
                  {/* The question is a real heading wrapping the toggle (the
                      WAI-ARIA accordion pattern) so each Q&A is addressable on
                      its own. `tracking-normal text-wrap` cancels the global
                      h1–h6 rules so the rendered question is unchanged. */}
                  <h3 className="tracking-normal text-wrap">
                    <button
                      id={headerId}
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-[clamp(15px,1.4vw,17px)] leading-snug font-medium text-ink transition-colors hover:bg-surface-2/40"
                    >
                      <span>{f.q}</span>
                      <span
                        className={cn(
                          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-[18px] leading-none transition-colors",
                          isOpen ? "border-accent text-accent" : "border-line text-ink-3",
                        )}
                        aria-hidden="true"
                      >
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>
                  </h3>
                  {/* The answer stays in the DOM whether or not the panel is
                      open, collapsed with a grid-row transition rather than
                      unmounted. Crawlers and AI assistants that read the
                      prerendered HTML then see the same answers the FAQPage
                      JSON-LD claims are on the page; `aria-hidden` keeps the
                      accessibility tree in step with what is visible. */}
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={headerId}
                    aria-hidden={!isOpen}
                    className={cn(
                      "grid transition-[grid-template-rows] duration-300 ease-out",
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="-mt-1 px-6 pb-6 text-[14.5px] leading-[1.65] text-ink-2">
                        {f.a}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
